import { useQuery } from '@apollo/client';
import React, { useMemo } from 'react';

import { ReactComponent as BSCLogo } from '../../../../../common/svg/sidebar/header/bsc-logo.svg';
import { ReactComponent as EthIcon } from '../../../../../common/svg/sidebar/header/icon-eth-small.svg';
import { ReactComponent as StarsIcon } from '../../../../../common/svg/sidebar/header/icon-stars-small.svg';
import type { useNetwork } from '../../../../../hooks';
import { useWeb3State } from '../../../../../hooks';
import { useContractAddress } from '../../../../../hooks/contract-address.hooks';
import { WEB3_CONFIG } from '../../../../../lib';
import type {
  Account,
  Listing,
} from '../../../../../lib/graph/MogulMarketplace';
import ownedAccountQuery from '../../../../../lib/graph/queries/accountByIdQuery';
import styles from '../styles.module.css';
import { Balance } from './balance';
import { DisconnectButton } from './disconnect-button';
import { NftButton } from './nft-button';

const LOGO_BY_CHAINKEY_MAP: Record<
  ReturnType<typeof useNetwork>['chainKey'],
  React.FC
> = {
  bsc: BSCLogo,
  eth: EthIcon,
};

interface QueryRes {
  account: Account | null;
}

interface Props {
  isOpen: boolean;
}

export const WalletInfo = ({ isOpen }: Props) => {
  const { walletDetails } = useWeb3State();
  const contractAddress = useContractAddress();
  const { data: walletData } = useQuery<QueryRes>(ownedAccountQuery, {
    variables: { addresses: walletDetails.address?.toLowerCase() },
  });
  const { data: marketplaceData } = useQuery<QueryRes>(ownedAccountQuery, {
    variables: {
      addresses: [
        contractAddress.MARKETPLACE_ADDRESS?.toLowerCase() ?? '',
        contractAddress.MARKETPLACE_ADDRESS_ERC_721?.toLowerCase() ?? '',
      ],
    },
  });

  const numOnWallet = walletData?.account?.numAssets ?? 0;

  const filteredAssets = useMemo(() => {
    if (!walletDetails.address || !marketplaceData?.account?.ownedAssets) {
      return [];
    }

    return marketplaceData.account.ownedAssets.reduce(
      (acc: Listing[], { asset }) => {
        const listings = asset.activeListings?.filter(
          l => l.seller.address === walletDetails.address!.toLowerCase(),
        );

        if (!listings) {
          return acc;
        }

        return acc.concat(listings ?? []);
      },
      [],
    );
  }, [marketplaceData?.account?.ownedAssets, walletDetails.address]);

  const nftsOwned = numOnWallet + filteredAssets.length;

  return (
    <div className={styles.WalletInfoProps}>
      <div className={styles.balanceRow}>
        {isOpen && (
          <>
            <Balance
              Icon={LOGO_BY_CHAINKEY_MAP[walletDetails.chain.key ?? 'bsc']}
              balance={walletDetails.balance.native.formatted}
              margin={walletDetails.chain.id === WEB3_CONFIG.chains.bsc.id}
            />
            <Balance
              Icon={StarsIcon}
              balance={walletDetails.balance.stars.formatted}
              margin
            />
          </>
        )}
      </div>
      <NftButton isOpen={isOpen} nftsOwnedCount={nftsOwned ? nftsOwned : 0} />
      <DisconnectButton isOpen={isOpen} />
    </div>
  );
};
